import { AuthContext, camelize } from '@repo-level/core-front';
import { tournaments } from 'api/cms/tournaments';
import type { TopPlayer } from 'api/tournaments';
import { userTournamentStatus } from 'api/tournaments';
import { useSubscription } from 'contexts/centrifugo/useSubscription';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { State } from './reducer';
import { ACTION_TYPE, reducer } from './reducer';

export const TournamentsContext = createContext<State>(null as any);

const initialState: State = {
  userTournamentsStatuses: [],
  cmsTournaments: [],
  pendingCMSTournaments: false,
};

export const TournamentsContextProvider: FC = ({ children }) => {
  const [tournamentsState, dispatchTournaments] = useReducer(
    reducer,
    initialState
  );

  const {
    i18n: { language },
  } = useTranslation();

  const { user } = useContext(AuthContext);

  const fetchUserTournamentStatus = useCallback(async () => {
    try {
      if (!user?.id) return;

      const { data: userTournamentsStatuses } = await userTournamentStatus();

      dispatchTournaments({
        type: ACTION_TYPE.SET_USER_TOURNAMENTS_STATUSES,
        payload: { userTournamentsStatuses },
      });
    } catch {}
    // key off id only: live changes arrive via the tournaments_statuses subscription
  }, [user?.id]);

  const fetchCMSTournaments = useCallback(async () => {
    try {
      dispatchTournaments({
        type: ACTION_TYPE.SET_PENDING_CMS_TOURNAMENTS_STATUS,
        payload: { pending: true },
      });

      const { data } = await tournaments(language);

      dispatchTournaments({
        type: ACTION_TYPE.SET_CMS_TOURNAMENTS,
        payload: { tournaments: data },
      });

      dispatchTournaments({
        type: ACTION_TYPE.SET_PENDING_CMS_TOURNAMENTS_STATUS,
        payload: { pending: false },
      });
    } catch {}
  }, [language]);

  useEffect(() => {
    fetchUserTournamentStatus();
  }, [fetchUserTournamentStatus]);

  useEffect(() => {
    fetchCMSTournaments();
  }, [fetchCMSTournaments]);

  // The channel publishes an array, same shape as GET /api/tournaments/statuses
  useSubscription<TopPlayer[]>('tournaments_statuses', (data) => {
    const statuses = camelize(data) as TopPlayer[];

    for (const status of statuses) {
      dispatchTournaments({
        type: ACTION_TYPE.UPDATE_USER_TOURNAMENT_STATUS,
        payload: { userTournamentStatus: status },
      });
    }
  });

  return (
    <TournamentsContext.Provider value={{ ...tournamentsState }}>
      {children}
    </TournamentsContext.Provider>
  );
};
